/* EveryNight reviews wall (QuietLab-style). Real reviews only: they load live from Judge.me's public widget feed
   (the same one its own widget uses) for every EveryNight product, because customers buy the 1/2/3-pack products
   and their reviews land there. Merged, photos first, then rating, then newest. The section stays hidden until
   the store has at least data-min reviews; the summary line uses every review, and "Show more" reaches all of them. */
(() => {
  if (window.enReviews) return;
  window.enReviews = true;

  const API = 'https://judge.me/reviews/reviews_for_widget';
  const PER = 30;
  const STAR = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2.8 2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8L12 2.8Z" fill="currentColor"/></svg>';
  const CHECK = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="m7 12.4 3.3 3.3L17.2 8.8" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const BADGES = { review_collected_from_another_provider: 'Imported review' };

  const cache = new Map();
  function load(shop, pid, page) {
    const q = new URLSearchParams({ url: shop, shop_domain: shop, platform: 'shopify', product_id: pid, page, per_page: PER, sort_by: 'pictures_first', sort_dir: 'desc' });
    const key = q.toString();
    if (!cache.has(key)) cache.set(key, fetch(`${API}?${key}`).then((r) => (r.ok ? r.json() : null)).then((j) => (j && Array.isArray(j.reviews) ? j : null)).catch(() => null));
    return cache.get(key);
  }
  const idList = (el) => [...new Set((el.dataset.ids || '').split(',').map((x) => x.trim()).filter(Boolean))];

  // First page of every product, merged: total and weighted average over all of them.
  async function summary(shop, ids) {
    const res = await Promise.all(ids.map((id) => load(shop, id, 1)));
    let total = 0;
    let sum = 0;
    const revs = [];
    res.forEach((d) => {
      if (!d) return;
      const n = parseInt(d.number_of_reviews, 10) || 0;
      total += n;
      sum += n * (parseFloat(d.average_rating) || 0);
      revs.push(...d.reviews);
    });
    return { total, avg: total ? sum / total : 0, revs };
  }

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  function stars(n) {
    const s = el('span', 'en-stars');
    const r = Math.round(n);
    s.setAttribute('role', 'img');
    s.setAttribute('aria-label', `${r} out of 5 stars`);
    for (let i = 1; i <= 5; i++) s.insertAdjacentHTML('beforeend', i <= r ? STAR : STAR.replace('<svg ', '<svg opacity=".25" '));
    return s;
  }

  // Review text arrives as HTML; keep only its plain-text paragraphs.
  function paras(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const ps = [...doc.body.querySelectorAll('p')].map((p) => p.textContent.trim()).filter(Boolean);
    if (ps.length) return ps;
    const t = doc.body.textContent.trim();
    return t ? [t] : [];
  }

  const pics = (r) => (r.pictures_urls || []).map((p) => (typeof p === 'string' ? { s: p, l: p } : {
    s: p.huge || p.original || p.compact || p.small, l: p.original || p.huge || p.compact || p.small,
  })).filter((p) => p.s);

  // "Robert Walker" -> "Robert W." (Judge.me may already send "Robert W.")
  function name(r) {
    const parts = String(r.reviewer_name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'Customer';
    if (parts.length === 1) return parts[0];
    const last = parts[parts.length - 1];
    return last.length <= 2 && last.endsWith('.') ? parts.join(' ') : `${parts[0]} ${last.charAt(0).toUpperCase()}.`;
  }

  function badges(r) {
    const b = r.transparency_badges;
    const keys = Array.isArray(b) ? b.map((x) => (typeof x === 'string' ? x : x && (x.key || x.type || x.name))) : b && typeof b === 'object' ? Object.keys(b).filter((k) => b[k]) : [];
    return keys.filter(Boolean).map((k) => BADGES[k] || (/incentiv/i.test(k) ? 'Incentivized review' : k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())));
  }

  function card(r, brand) {
    const c = el('figure', 'en-rvw__card');
    const p = pics(r)[0];
    if (p) {
      const a = el('a', 'en-rvw__pica');
      a.href = p.l;
      a.target = '_blank';
      a.rel = 'noopener';
      const img = el('img', 'en-rvw__pic');
      img.src = p.s;
      img.alt = 'Customer photo';
      img.loading = 'lazy';
      a.append(img);
      c.append(a);
    }
    const inner = el('div', 'en-rvw__in');
    const top = el('div', 'en-rvw__top');
    const who = el('div', 'en-rvw__who');
    who.append(el('b', '', name(r)));
    if (r.verified_buyer === true) {
      const v = el('span', 'en-rvw__v');
      v.innerHTML = CHECK;
      v.append('Verified purchase');
      who.append(v);
    }
    top.append(who, stars(r.rating));
    inner.append(top);
    if (r.title) inner.append(el('b', 'en-rvw__title', r.title));
    const body = el('blockquote', 'en-rvw__body');
    paras(r.body_html).forEach((t) => body.append(el('p', '', t)));
    inner.append(body);
    const tags = badges(r);
    if (r.product_variant_title || tags.length) inner.append(el('small', 'en-rvw__meta', [r.product_variant_title, ...tags].filter(Boolean).join(' · ')));
    if (r.reply_content) {
      const rep = el('div', 'en-rvw__reply');
      rep.append(el('b', '', `Reply from ${r.shop_reply_name || brand}`));
      paras(r.reply_content).forEach((t) => rep.append(el('p', '', t)));
      inner.append(rep);
    }
    c.append(inner);
    return c;
  }

  // Long reviews start clamped with a "Read more" toggle.
  function clamp(c) {
    const body = c.querySelector('.en-rvw__body');
    if (!body || body.scrollHeight <= body.clientHeight + 4) { body && body.classList.remove('is-clamped'); return; }
    const b = el('button', 'en-rvw__more', 'Read more');
    b.type = 'button';
    b.addEventListener('click', () => { body.classList.remove('is-clamped'); b.remove(); });
    body.after(b);
  }

  const score = (r) => (pics(r).length ? 1e13 : 0) + (r.rating || 0) * 1e12 + (Date.parse(r.created_at) || 0) / 10;
  const keyOf = (r) => r.id || r.uuid || `${r.reviewer_name}|${r.created_at}|${r.title}`;

  async function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const shop = root.dataset.shop;
    const ids = [...new Set((root.dataset.ids || '').split(',').map((x) => x.trim()).filter(Boolean))];
    const min = parseInt(root.dataset.min, 10) || 3;
    const step = matchMedia('(max-width: 699px)').matches ? Math.min(4, parseInt(root.dataset.first, 10) || 9) : parseInt(root.dataset.first, 10) || 9;
    const brand = root.dataset.brand || 'us';
    if (!shop || !ids.length) return;

    const state = ids.map((pid) => ({ pid, page: 0, pages: 1 }));
    const seen = new Set();
    let pool = [];
    let total = 0;
    let sum = 0;

    async function fetchMore() {
      const todo = state.filter((s) => s.page < s.pages);
      const res = await Promise.all(todo.map((s) => load(shop, s.pid, s.page + 1)));
      const fresh = [];
      res.forEach((d, i) => {
        const s = todo[i];
        s.page += 1;
        if (!d) { s.pages = 0; return; }
        if (s.page === 1) {
          const n = parseInt(d.number_of_reviews, 10) || 0;
          total += n;
          sum += n * (parseFloat(d.average_rating) || 0);
        }
        s.pages = (d.pagination && d.pagination.total_pages) || 1;
        d.reviews.forEach((r) => {
          const k = keyOf(r);
          if (seen.has(k) || (!r.title && !paras(r.body_html).length)) return;
          seen.add(k);
          fresh.push(r);
        });
      });
      pool = pool.concat(fresh.sort((a, b) => score(b) - score(a)));
    }

    await fetchMore();
    const manual = parseInt(root.dataset.manual, 10) || 0;
    if (total < min && !manual) return;

    const wall = root.querySelector('[data-en-rv-wall]');
    const more = root.querySelector('[data-en-rv-more]');
    const sumEl = root.querySelector('[data-en-rv-sum]');
    if (total) {
      const avg = sum / total;
      sumEl.replaceChildren(stars(avg), el('span', '', `${avg.toFixed(1)} average · ${total.toLocaleString('en-US')} review${total === 1 ? '' : 's'}`));
      sumEl.hidden = false;
    }
    root.hidden = false;

    // Masonry: each card goes into the shortest column, so the best (photo) reviews spread across the top row.
    const cards = [...wall.querySelectorAll(':scope > .en-rvw__card')];
    let cols = [];
    const count = () => {
      const w = wall.clientWidth || wall.parentElement.clientWidth;
      return w >= 940 ? 3 : w >= 600 ? 2 : 1;
    };
    function place(list) {
      list.forEach((c) => {
        const col = cols.reduce((a, b) => (b.offsetHeight < a.offsetHeight ? b : a));
        col.append(c);
      });
    }
    function layout() {
      cols = Array.from({ length: count() }, () => el('div', 'en-rvw__col'));
      wall.replaceChildren(...cols);
      wall.classList.add('is-masonry');
      place(cards);
    }
    layout();
    let lastN = cols.length;
    addEventListener('resize', () => { if (count() !== lastN) { layout(); lastN = cols.length; } });

    let shown = 0;
    async function show(n) {
      while (pool.length - shown < n && state.some((s) => s.page < s.pages)) await fetchMore();
      const batch = pool.slice(shown, shown + n).map((r) => card(r, brand));
      shown += batch.length;
      batch.forEach((c) => c.querySelector('.en-rvw__body').classList.add('is-clamped'));
      cards.push(...batch);
      place(batch);
      batch.forEach(clamp);
      more.hidden = !(shown < pool.length || state.some((s) => s.page < s.pages));
    }
    more.addEventListener('click', async () => {
      more.disabled = true;
      await show(step);
      more.disabled = false;
    });
    await show(step);
  }

  // Buy box: star rating line under the title.
  async function initRating(a) {
    if (a.dataset.ready) return;
    a.dataset.ready = '1';
    const s = await summary(a.dataset.shop, idList(a));
    if (s.total < (parseInt(a.dataset.min, 10) || 3)) return;
    const txt = el('span');
    txt.append(el('b', '', s.avg.toFixed(1)), ` · ${s.total.toLocaleString('en-US')} review${s.total === 1 ? '' : 's'}`);
    a.replaceChildren(stars(s.avg), txt);
    a.hidden = false;
  }

  // Buy box: one real 5-star review under the button (verified buyers first, then newest).
  async function initFeatured(f) {
    if (f.dataset.ready) return;
    f.dataset.ready = '1';
    const s = await summary(f.dataset.shop, idList(f));
    if (s.total < (parseInt(f.dataset.min, 10) || 3)) return;
    const text = (r) => paras(r.body_html).join(' ');
    const pick = s.revs
      .filter((r) => r.rating === 5 && text(r).length >= 40 && !badges(r).length)
      .sort((a, b) => (b.verified_buyer === true) - (a.verified_buyer === true) || (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0))[0];
    if (!pick) return;
    let t = text(pick);
    if (t.length > 230) t = `${t.slice(0, 230).replace(/\s+\S*$/, '')}…`;
    const cap = el('figcaption');
    cap.append(el('b', '', name(pick)));
    if (pick.verified_buyer === true) {
      const v = el('span', 'en-featrev__v');
      v.innerHTML = CHECK;
      v.append('Verified purchase');
      cap.append(v);
    }
    f.replaceChildren(stars(5), el('blockquote', '', t), cap);
    f.hidden = false;
  }

  // Video reviews (sections/en-video-reviews.liquid): customer videos from the section's blocks, real written
  // Judge.me reviews mixed in between them, silent previews while on screen, a full-screen player with sound.
  function textCard(r) {
    const c = el('figure', 'en-vr__card en-vr__card--text');
    const box = el('div', 'en-vr__text');
    box.append(stars(r.rating));
    let t = paras(r.body_html).join(' ');
    if (r.title && !t.toLowerCase().startsWith(String(r.title).toLowerCase())) t = `${r.title}. ${t}`;
    box.append(el('blockquote', 'en-vr__quote', `“${t}”`));
    const who = el('span', 'en-vr__who');
    who.append(el('b', '', name(r)));
    if (r.verified_buyer === true) {
      const v = el('span', 'en-rvw__v');
      v.innerHTML = CHECK;
      v.append('Verified purchase');
      who.append(v);
    }
    box.append(who);
    if (r.product_variant_title) box.append(el('small', '', r.product_variant_title));
    c.append(box);
    return c;
  }

  async function initVideo(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const track = root.querySelector('[data-en-vr-track]');
    const vids = [...track.querySelectorAll('.en-vr__card--vid')];
    if (!vids.length) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    // silent previews: only the cards on screen play
    const io = 'IntersectionObserver' in window && !reduce ? new IntersectionObserver((ents) => ents.forEach((e) => {
      const v = e.target.querySelector('video');
      if (!v) return;
      if (e.isIntersecting && e.intersectionRatio >= 0.6) {
        if (v.preload === 'none') v.preload = 'metadata';
        v.muted = true;
        v.play().catch(() => {});
      } else v.pause();
    }), { threshold: [0, 0.6] }) : null;
    vids.forEach((c) => io && io.observe(c));

    // arrows (desktop): one card per click
    const by = () => (track.querySelector('.en-vr__card') || {}).offsetWidth + 16 || 280;
    const prev = root.querySelector('[data-en-vr-prev]');
    const next = root.querySelector('[data-en-vr-next]');
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -by(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: by(), behavior: 'smooth' }));

    // full-screen player
    const dlg = root.querySelector('[data-en-vr-dlg]');
    const player = dlg.querySelector('[data-en-vr-player]');
    const dlgBy = dlg.querySelector('[data-en-vr-dlgby]');
    let idx = 0;
    function open(i) {
      idx = (i + vids.length) % vids.length;
      const card = vids[idx];
      const v = card.querySelector('video');
      player.pause();
      player.removeAttribute('src');
      const srcs = [...v.querySelectorAll('source')].map((s) => s.cloneNode());
      player.replaceChildren(...srcs);
      if (!srcs.length) player.src = v.currentSrc || v.getAttribute('src');
      player.poster = v.poster || '';
      player.load();
      player.muted = false;
      const who = card.querySelector('.en-vr__who b');
      dlgBy.textContent = who ? who.textContent : '';
      vids.forEach((c) => { const pv = c.querySelector('video'); if (pv) pv.pause(); });
      if (!dlg.open) dlg.showModal();
      player.play().catch(() => {});
    }
    function close() {
      player.pause();
      if (dlg.open) dlg.close();
    }
    vids.forEach((c, i) => c.querySelector('[data-en-vr-open]').addEventListener('click', () => open(i)));
    dlg.querySelector('[data-en-vr-close]').addEventListener('click', close);
    dlg.querySelector('[data-en-vr-dprev]').addEventListener('click', () => open(idx - 1));
    dlg.querySelector('[data-en-vr-dnext]').addEventListener('click', () => open(idx + 1));
    dlg.addEventListener('click', (e) => { if (e.target === dlg) close(); });
    dlg.addEventListener('close', () => player.pause());
    if (vids.length < 2) dlg.querySelectorAll('[data-en-vr-dprev],[data-en-vr-dnext]').forEach((b) => { b.hidden = true; });

    // real written reviews from Judge.me, one after each video (verified buyers first, then newest)
    const shop = root.dataset.shop;
    const ids = idList(root);
    const maxText = parseInt(root.dataset.maxText, 10) || 0;
    if (!shop || !ids.length) return;
    const s = await summary(shop, ids);
    if (s.total >= (parseInt(root.dataset.min, 10) || 3)) {
      const sumEl = root.querySelector('[data-en-rv-sum]');
      sumEl.replaceChildren(stars(s.avg), el('span', '', `${s.avg.toFixed(1)} average · ${s.total.toLocaleString('en-US')} review${s.total === 1 ? '' : 's'}`));
      sumEl.hidden = false;
    }
    if (!maxText) return;
    const text = (r) => paras(r.body_html).join(' ');
    const picks = s.revs
      .filter((r) => (r.rating || 0) >= 4 && text(r).length >= 40 && !badges(r).length)
      .sort((a, b) => (b.verified_buyer === true) - (a.verified_buyer === true) || (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0))
      .slice(0, maxText);
    picks.forEach((r, i) => {
      const c = textCard(r);
      if (vids[i]) vids[i].after(c);
      else track.append(c);
    });
  }

  function boot() {
    document.querySelectorAll('[data-en-reviews]').forEach(init);
    document.querySelectorAll('[data-en-rating]').forEach(initRating);
    document.querySelectorAll('[data-en-featured]').forEach(initFeatured);
    document.querySelectorAll('[data-en-vreviews]').forEach(initVideo);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-en-reviews]').forEach(init);
    e.target.querySelectorAll('[data-en-vreviews]').forEach(initVideo);
  });
})();
